/**
 * Feature flags service
 * Migrated to use settingsStore for sync support
 * Note: Feature flags are managed server-side, server always wins during sync
 */

import { settingsStore } from './settingsStore';

export const FEATURE_FLAG_AI_DESCRIBE = 'ai_describe';
export const FEATURE_FLAG_AI_PHOTOS = 'ai_photos';
export const FEATURE_FLAG_REAL_TIME_SYNC = 'real_time_sync';
export const FEATURE_FLAG_INTERVALS_ICU_SYNC = 'intervals_icu_sync';

export type FeatureFlagDefinition = {
  key: string;
  defaultValue: boolean;
  label: string;
  description: string;
};

const FEATURE_FLAG_DEFINITIONS: FeatureFlagDefinition[] = [
  {
    key: FEATURE_FLAG_AI_DESCRIBE,
    defaultValue: true,
    label: 'AI Describe',
    description: 'Enables AI-assisted food description parsing.',
  },
  {
    key: FEATURE_FLAG_AI_PHOTOS,
    defaultValue: false,
    label: 'AI Photos',
    description: 'Enables AI-assisted food photo processing.',
  },
  {
    key: FEATURE_FLAG_REAL_TIME_SYNC,
    defaultValue: false,
    label: 'Real-Time Sync',
    description: 'Enables real-time data sync between devices.',
  },
  {
    key: FEATURE_FLAG_INTERVALS_ICU_SYNC,
    defaultValue: false,
    label: 'Intervals.icu Sync',
    description: 'Enables Intervals.icu workout connection and synchronization.',
  },
];

const FEATURE_FLAG_DEFINITIONS_BY_KEY = FEATURE_FLAG_DEFINITIONS.reduce(
  (result: Record<string, FeatureFlagDefinition>, definition) => {
    result[definition.key] = definition;
    return result;
  },
  {},
);

const DEFAULT_FEATURE_FLAG_VALUES: Record<string, boolean> = {
  ...FEATURE_FLAG_DEFINITIONS.reduce((result: Record<string, boolean>, definition) => {
    result[definition.key] = definition.defaultValue;
    return result;
  }, {}),
};

const getDefaultFeatureFlagValue = (flag: string): boolean =>
  DEFAULT_FEATURE_FLAG_VALUES[flag] ?? false;

export const listFeatureFlagDefinitions = (): FeatureFlagDefinition[] =>
  FEATURE_FLAG_DEFINITIONS.map((definition) => ({ ...definition }));

export const getFeatureFlagDefinition = (flag: string): FeatureFlagDefinition | null => {
  const definition = FEATURE_FLAG_DEFINITIONS_BY_KEY[flag];
  return definition ? { ...definition } : null;
};

/**
 * Get a feature flag value
 * Returns the configured default value if the flag doesn't exist
 */
export async function getFeatureFlag(flag: string): Promise<boolean> {
  return await settingsStore.get<boolean>(
    "featureFlag",
    flag,
    getDefaultFeatureFlagValue(flag),
  );
}

/**
 * Set a feature flag value
 * Note: Server will override this value during sync (server wins)
 * Client-side changes are temporary until next sync
 */
export async function setFeatureFlag(flag: string, value: boolean): Promise<void> {
  await settingsStore.save<boolean>("featureFlag", flag, value);
}

/**
 * Get all feature flags
 */
export async function getAllFeatureFlags(): Promise<Record<string, boolean>> {
  const flags = await settingsStore.getByType("featureFlag");
  const result: Record<string, boolean> = {};
  
  flags.forEach((flag) => {
    // Skip the type prefix in the id (format: "featureFlag:flagName")
    const key = flag.key;
    const flagValue = flag.value as boolean;
    result[key] = flagValue;
  });
  
  return result;
}

/**
 * Clear all feature flags
 * Note: Server will repopulate them during next sync
 */
export async function clearAllFeatureFlags(): Promise<void> {
  const flags = await settingsStore.getByType("featureFlag");
  for (const flag of flags) {
    await settingsStore.delete("featureFlag", flag.key);
  }
}
